import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { SearchPage } from "@mirage/mosaics/SearchPage";
import {
  generateSearchURL,
  getSearchFromURLParams,
  useQueryParams,
} from "@mirage/search/hooks/useQueryParams";
import useConnectors from "@mirage/service-connectors/useConnectors";
import { useSubmitFeedback } from "@mirage/service-feedback/hooks/useSubmitFeedback";
import { useSearchContext } from "@mirage/service-search/hooks";
import { TypeaheadQueryAtom } from "@mirage/service-typeahead-search/hooks/useTypeaheadSearch";
import { useDashTitle } from "@mirage/shared/hooks/DashTitle";
import { useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useNavigationFocus } from "../Layout/NavigationFocus";

import type { SearchOptions } from "@mirage/service-search/service";
import type { SearchFilter } from "@mirage/shared/search/search-filters";
import { ComposeAnalyticsContextProvider } from "../ComposeAssistant/data/ComposeAnalyticsContext";

interface SearchResultsPageProps {
  isDesktop?: boolean;
}

export const SearchResultsPage = ({ isDesktop }: SearchResultsPageProps) => {
  const { connectors } = useConnectors();
  const navigate = useNavigate();
  const { handleSearch: performSearch, ...rest } = useSearchContext();
  const { searchAttemptSessionManager } = useMirageAnalyticsContext();
  const queryParams = useQueryParams();
  const { query, filters, searchOptions } = useMemo(
    () => getSearchFromURLParams(queryParams),
    [queryParams],
  );
  const setTypeaheadQuery = useSetAtom(TypeaheadQueryAtom);
  const { excludeNavFocusPath } = useNavigationFocus();
  const { addFeedbackParams } = useSubmitFeedback();

  useDashTitle(query);

  useEffect(() => {
    excludeNavFocusPath("/search_results");
  }, [excludeNavFocusPath]);

  useEffect(() => {
    addFeedbackParams({
      query: rest.query,
      filters: rest.filters.map((filter) => filter.id),
    });
  }, [rest.query, rest.filters]);

  useEffect(() => {
    if (!query && !filters.length) return;
    performSearch(query, filters, searchOptions);
  }, [query, filters, searchOptions]);

  useEffect(() => {
    return () => {
      rest.resetQuery();
    };
  }, [rest.resetQuery]);

  const handleSearch = useCallback(
    async (
      query: string,
      filters?: SearchFilter[] | undefined,
      options?: Partial<SearchOptions>,
    ) => {
      searchAttemptSessionManager.updateProperties({
        searchSurface: "result_page",
      });
      setTypeaheadQuery(query);
      navigate(generateSearchURL(query, filters, options));
    },
    [navigate, searchAttemptSessionManager, setTypeaheadQuery],
  );

  const handleResubmitSearch = useCallback(() => {
    performSearch(rest.query, rest.filters);
  }, [performSearch, rest.filters, rest.query]);

  return (
    <ComposeAnalyticsContextProvider>
      <SearchPage
        searchContext={{
          handleSearch,
          ...rest,
        }}
        connectors={connectors}
        isDesktop={isDesktop}
        onResubmitSearch={handleResubmitSearch}
        setTypeaheadQuery={setTypeaheadQuery}
      />
    </ComposeAnalyticsContextProvider>
  );
};
